import React, { useEffect, useRef, useState } from "react";
import * as d3 from "d3";
import { CONFIG } from "../config";

const TopFailuresGraph = ({ selectedDate, selectedStatus, selectedAgeRange, selectedFailedComponent, onSelectedFailedComponentChange }) => {
  const containerRef = useRef();
  const svgRef = useRef();
  const rowHeight = 70;

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      const { width } = entry.contentRect;
      setDimensions(prev => {
        if (prev.width !== width) {
          return { ...prev, width };
        }
        return prev;
      });
    });

    if (containerRef.current) observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!selectedDate) return;

    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await fetch(
          `${CONFIG.API_BASE_URL}/${CONFIG.TOP_FAILURES_PREDICTION_GRAPH_URL}/${localStorage.getItem("tensor-user-id")}?date_str=${selectedDate}&selected_status=${selectedStatus}&selected_agerange=${selectedAgeRange}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: "Bearer " + localStorage.getItem("tensor-auth-token"),
            },
          }
        );

        const result = await response.json();
        if (result.success && Array.isArray(result.data)) {
          setData(result.data);
        } else {
          setData([]);
        }
      } catch (error) {
        console.error("Failed to fetch top failures data:", error);
        setData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [selectedDate, selectedStatus, selectedAgeRange]);

  useEffect(() => {
    if (!data.length || !dimensions.width) return;

    const drawChart = () => {
      const svg = d3.select(svgRef.current);
      svg.selectAll("*").remove();

      const fullWidth = svgRef.current?.parentElement?.offsetWidth || 500;
      const margin = { top: 30, right: 30, bottom: 10, left: 280 }; // Slightly reduced left margin
      const height = margin.top + data.length * rowHeight + margin.bottom;
      const barWidth = fullWidth - margin.left - margin.right;
      const dynamicHeight = margin.top + data.length * rowHeight + margin.bottom;

      svg
        .attr("width", fullWidth)
        .attr("height", dynamicHeight)
        .attr("viewBox", `0 0 ${fullWidth} ${dynamicHeight}`)
        .attr("preserveAspectRatio", "xMinYMin meet");

      const maxValue = d3.max(data, d => d.last6 + d.next4);
      const xScale = d3.scaleLinear().domain([0, maxValue]).range([0, barWidth]);
      const yScale = d3
        .scaleBand()
        .domain(data.map(d => d.issue))
        .range([margin.top, height - margin.bottom])
        .padding(0.5);

      const g = svg.append("g").attr("transform", `translate(${margin.left}, 0)`);

      // Text wrapping function
      const wrapText = (text, width) => {
        text.each(function () {
          const text = d3.select(this);
          const words = text.text().split(/\s+/).reverse();
          let word;
          let line = [];
          let lineNumber = 0;
          const lineHeight = 1.1; // ems
          const y = text.attr("y");
          const dy = parseFloat(text.attr("dy") || 0);
          let tspan = text
            .text(null)
            .append("tspan")
            .attr("x", text.attr("x"))
            .attr("y", y)
            .attr("dy", dy + "em");

          while ((word = words.pop())) {
            line.push(word);
            tspan.text(line.join(" "));
            if (tspan.node().getComputedTextLength() > width && line.length > 1) {
              line.pop();
              tspan.text(line.join(" "));
              line = [word];
              tspan = text
                .append("tspan")
                .attr("x", text.attr("x"))
                .attr("y", y)
                .attr("dy", ++lineNumber * lineHeight + dy + "em")
                .text(word);
            }
          }
          // Adjust y-position to center the wrapped text vertically
          const tspans = text.selectAll("tspan");
          const totalLines = tspans.size();
          if (totalLines > 1) {
            tspans.each(function (d, i) {
              d3.select(this).attr("dy", (i - (totalLines - 1) / 2) * lineHeight + dy + "em");
            });
          }
        });
      };

      // Render issue labels with wrapping
      // svg
      //   .selectAll(".issue-label")
      //   .data(data)
      //   .enter()
      //   .append("text")
      //   .attr("x", 5)
      //   .attr("y", d => yScale(d.issue) + yScale.bandwidth() / 2)
      //   .attr("dy", "0.35em") // Slight adjustment for vertical centering
      //   .style("font-family", "Noto Sans")
      //   .style("font-size", "16px")
      //   .style("font-style", "normal")
      //   .style("font-weight", 400)
      //   .style("fill", "#1C2024")
      //   .attr("alignment-baseline", "middle")
      //   .text((d, i) => `${i + 1}. ${d.issue}`)
      //   .call(wrapText, margin.left - 10); // Wrap text within margin.left minus padding

       // Render issue labels with wrapping
    const issueText = svg
    .selectAll(".issue-label")
    .data(data)
    .enter()
    .append("text")
    .attr("class", "issue-label")
    .attr("x", 5)
    .attr("y", d => yScale(d.issue) + yScale.bandwidth() / 2)
    .attr("dy", "0.35em") // Vertical centering
    .style("font-family", "Noto Sans")
    .style("font-size", "16px")
    .style("font-style", "normal")
    .style("font-weight", 400)
    .style("fill", "#1C2024")
    .attr("alignment-baseline", "middle")
    .text((d, i) => {
      const label = `${i + 1}. ${d.issue}`;
      return label.length > 30 ? label.slice(0, 27) + "..." : label;
    });

    // Add full issue name as tooltip
    issueText
    .append("title")
    .text((d, i) => `${i + 1}. ${d.issue}`);



      g.selectAll(".bar-last6")
        .data(data)
        .enter()
        .append("rect")
        .attr("x", 0)
        .attr("y", d => yScale(d.issue))
        .attr("width", d => xScale(d.last6))
        .attr("height", yScale.bandwidth())
        .attr("fill", "#0037FF")
        .style("cursor", "pointer")
        .style("opacity", d => {
          if (!selectedFailedComponent) return 1;
          const key = `${d.issue} (Last 6 Weeks)`;
          return selectedFailedComponent.replace(":last6", "") === d.issue ? 1 : 0.4;
        })
        .on("click", (event, d) => {
          if (onSelectedFailedComponentChange) {
            onSelectedFailedComponentChange(`${d.issue}:last6`);
          }
        });

      g.selectAll(".bar-next4")
        .data(data)
        .enter()
        .append("rect")
        .attr("x", d => xScale(d.last6))
        .attr("y", d => yScale(d.issue))
        .attr("width", d => xScale(d.next4))
        .attr("height", yScale.bandwidth())
        .attr("fill", "#7CA6FF")
        .style("cursor", "pointer")
        .style("opacity", d => {
          if (!selectedFailedComponent) return 1;
          const key = `${d.issue} (Next 4 Weeks)`;
          return selectedFailedComponent.replace(":next4", "") === d.issue ? 1 : 0.4;
        })
        .on("click", (event, d) => {
          if (onSelectedFailedComponentChange) {
            onSelectedFailedComponentChange(`${d.issue}:next4`);
          }
        });

      g.selectAll(".text-last6")
        .data(data)
        .enter()
        .append("text")
        .attr("x", -30)
        .attr("y", d => yScale(d.issue) + yScale.bandwidth() / 2 + 4)
        .attr("fill", "#0037FF")
        .style("font-family", "Noto Sans")
        .style("font-size", "16px")
        .style("font-style", "normal")
        .attr("font-weight", "700")
        .text(d => d.last6);
       

      g.selectAll(".text-next4")
        .data(data)
        .enter()
        .append("text")
        .attr("x", d => xScale(d.last6 + d.next4) + 5)
        .attr("y", d => yScale(d.issue) + yScale.bandwidth() / 2 + 4)
        .attr("fill", "#7CA6FF")
        .style("font-family", "Noto Sans")
        .style("font-size", "16px")
        .style("font-style", "normal")
        .attr("font-weight", "700")
        .text(d => d.next4);
    };

    drawChart();
    window.addEventListener("resize", drawChart);
    return () => window.removeEventListener("resize", drawChart);
  }, [data, dimensions, selectedFailedComponent]);

  return (
    <div ref={containerRef}>
      <h2 className="text-xl font-semibold mb-4 text-2xl font-bold truck_page_head_text">
        Top Failures (Actual vs. Predicted)
      </h2>
      {loading && (
        <div className="flex justify-center items-center min-h-[180px]">
          <svg
            className="w-6 h-6 text-blue-500 animate-spin"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="2"
            ></circle>
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
            ></path>
          </svg>
        </div>
      )}
      {!loading && (
        <div className="pt-4 pr-4 pl-4 pb-0 bg-white min-h-[300px] relative">
          {data.length > 0 && (
            <div className="flex justify-between items-center px-2 pt-2 pb-1">
              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-1">
                  <div className="w-3 h-3 bg-[#0037FF]"></div>
                  <span className="text-sm text-gray-800">Last 6 weeks</span>
                </div>
                <div className="flex items-center space-x-1">
                  <div className="w-3 h-3 bg-[#7CA6FF]"></div>
                  <span className="text-sm text-gray-800">Next 4 weeks</span>
                </div>
              </div>
              <div>
                {(() => {
                  const restrictedIds = JSON.parse(process.env.REACT_APP_RESTRICTED_USER_ID || "[]");
                  const userId = parseInt(localStorage.getItem("tensor-user-id"), 10);
                  //const isRestricted = deviceProvider === restrictedDevice;
                  const restrictedDevice = process.env.REACT_APP_RESTRICTED_DEVICE || '';
                  const deviceProvider = localStorage.getItem("tensor-user-device-provider");
                  const isRestricted = deviceProvider === restrictedDevice;
                  const selectedValue = isRestricted ? "Exhaust" : "";

                  return (
                    <select
                      className="border border-gray-300 rounded px-2 py-1 text-sm bg-[#e8efff] text-gray-800"
                      value={selectedValue}
                      onChange={() => {}}
                    >
                      <option value="" disabled={isRestricted}>All</option>
                      <option value="Exhaust" disabled={!isRestricted}>Exhaust</option>
                    </select>
                  );
                })()}
              </div>
            </div>
          )}
          <div className="w-full overflow-x-auto">
            <svg ref={svgRef}></svg>
          </div>
        </div>
      )}
    </div>
  );
};

export default TopFailuresGraph;